import { useEffect, useMemo, useState } from "react";
import { Search, X } from "react-feather";
import "./PortalProjectsCenter.css";

function formatDate(value) {
    if (!value) return null;

    const d = new Date(value);

    if (Number.isNaN(d.getTime())) {
        return null;
    }

    return d.toLocaleString();
}

function stripHtmlToText(html) {
    if (!html) return "";

    return String(html)
        .replace(/<style[\s\S]*?<\/style>/gi, "")
        .replace(/<script[\s\S]*?<\/script>/gi, "")
        .replace(/<\/(p|div|br|li|h1|h2|h3|h4|h5|h6)>/gi, "\n")
        .replace(/<li[^>]*>/gi, "• ")
        .replace(/<[^>]+>/g, "")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}

function getPreviewImage(project) {

    return (
        project.aiImages?.[0]?.url ||
        project.aiImages?.find(img => img.url)?.url ||
        project.previewImage ||
        project.renderImage ||
        project.imageUrl ||
        project.images?.[0]?.url ||
        null
    );

}

export default function PortalProjectsCenter({
    open,
    onClose,
    projects = [],
    initialSearch = "",
    companyId,
    onOpenRevision
}) {

    const [expanded, setExpanded] = useState({});
    const [search, setSearch] = useState(initialSearch);
    const [descriptionHtml, setDescriptionHtml] = useState("");
    const [loadingProjectId, setLoadingProjectId] = useState(null);

    useEffect(() => {

        setSearch(initialSearch);

    }, [initialSearch, open]);

    const filteredProjects = useMemo(() => {

        const term = search.toLowerCase().trim();

        if (!term) {
            return projects;
        }

        return projects.filter((project) => {

            return (

                (project.projectName || "")
                    .toLowerCase()
                    .includes(term)

                ||

                (project.contact?.email || "")
                    .toLowerCase()
                    .includes(term)

                ||

                (project.status || "")
                    .toLowerCase()
                    .includes(term)

            );

        });

    }, [projects, search]);


    function toggleExpand(projectId) {

        setExpanded(prev => ({
            ...prev,
            [projectId]: !prev[projectId]
        }));

    }

    if (!open) {
        return null;
    }

    return (

        <div
            className="ppm-backdrop"
            onClick={onClose}
        >

            <div
                className="ppm-center"
                onClick={(e) => e.stopPropagation()}
            >

                <div className="ppm-header">

                    <div>

                        <h2>
                            Projects
                        </h2>

                        <span className="ppm-count">
                            {filteredProjects.length} project{filteredProjects.length !== 1 ? "s" : ""}
                        </span>

                    </div>

                    <button
                        className="ppm-close"
                        onClick={onClose}
                    >
                        <X size={18} />
                    </button>

                </div>

                <div className="ppm-search">

                    <Search size={16} />

                    <input
                        placeholder="Search by project name, email or status..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />

                </div>

                <div className="projects-grid">
                    {filteredProjects.map((project) => {

                        const projectKey = project.projectId || project.id;
                        const isOpen = expanded[projectKey];

                        const preview = getPreviewImage(project);

                        const createdAt = formatDate(project.createdAt);
                        const completedAt = formatDate(project.completedAt);
                        const aiGeneratedAt = formatDate(project.aiText?.generatedAt);

                        const contactEmail =
                            project.contact?.email ||
                            project.email ||
                            "";

                        const proposalUrl =
                            project.pdf?.url ||
                            project.pdfUrl ||
                            "";

                        const proposalName =
                            project.pdf?.fileName ||
                            "proposal.pdf";

                        const sowUrl =
                            project.prePdf?.url ||
                            "";

                        const sowName =
                            project.prePdf?.fileName ||
                            "setup.pdf";

                        const status =
                            (project.status || "unknown").toLowerCase();

                        const plainDesc =
                            stripHtmlToText(project.aiText?.html || "");

                        return (

                            <div
                                key={projectKey}
                                className="project-card"
                            >

                                <div className="project-image">

                                    {preview ? (

                                        <img
                                            src={preview}
                                            alt={project.projectName}
                                        />

                                    ) : (

                                        <div className="project-no-image">
                                            No Preview
                                        </div>

                                    )}

                                </div>

                                <div className="project-body">

                                    <h3>
                                        {project.projectName || "Untitled Project"}
                                    </h3>

                                    <div className="project-meta">

                                        <span className={`status ${status}`}>
                                            {project.status || "Pending"}
                                        </span>

                                        {completedAt && (
                                            <span>
                                                {completedAt}
                                            </span>
                                        )}

                                    </div>

                                    <div className="project-kv">

                                        {createdAt && (

                                            <div className="kv">

                                                <span className="k">
                                                    Created
                                                </span>

                                                <span className="v">
                                                    {createdAt}
                                                </span>

                                            </div>

                                        )}

                                        {contactEmail && (

                                            <div className="kv">

                                                <span className="k">
                                                    Email
                                                </span>

                                                <span className="v mono">
                                                    {contactEmail}
                                                </span>

                                            </div>

                                        )}

                                        {aiGeneratedAt && (

                                            <div className="kv">

                                                <span className="k">
                                                    AI
                                                </span>

                                                <span className="v">

                                                    {aiGeneratedAt}

                                                    {project.aiText?.model &&
                                                        ` • ${project.aiText.model}`}

                                                </span>

                                            </div>

                                        )}

                                    </div>

                                    <div className="project-actions">

                                        {proposalUrl && (

                                            <a
                                                href={proposalUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Proposal

                                                <span className="file-hint">
                                                    {proposalName}
                                                </span>

                                            </a>

                                        )}

                                        {sowUrl && (

                                            <a
                                                href={sowUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                SOW

                                                <span className="file-hint">
                                                    {sowName}
                                                </span>

                                            </a>

                                        )}


                                        {companyId && (project.projectId || project.id) && onOpenRevision && (
                                            <button
                                                className="ai-editor-btn"
                                                disabled={loadingProjectId === project.projectId}
                                                onClick={async () => {

                                                    setLoadingProjectId(project.projectId);

                                                    try {

                                                        await onOpenRevision(
                                                            companyId,
                                                            project.projectId || project.id
                                                        );

                                                    } finally {

                                                        setLoadingProjectId(null);

                                                    }

                                                }}
                                            >
                                                {loadingProjectId === project.projectId
                                                    ? "Loading..."
                                                    : "✨ AI Editor"}
                                            </button>
                                        )}

                                        <button
                                            className="portal-primary-btn"
                                            onClick={() => setDescriptionHtml(project.aiText?.html || "")}
                                        >
                                            View AI Description
                                        </button>

                                    </div>

                                    {(project.projectId || project.id) && (

                                        <div className="project-id">

                                            ID: {project.projectId || project.id}

                                        </div>

                                    )}

                                </div>

                            </div>

                        );

                    })}

                </div> {/* projects-grid */}

            </div> {/* ppm-center */}

            {descriptionHtml && (

                <div
                    className="ppm-description-backdrop"
                    onClick={() => setDescriptionHtml("")}
                >

                    <div
                        className="ppm-description-modal"
                        onClick={(e) => e.stopPropagation()}
                    >

                        {/* <button
                            className="ppm-close"
                            onClick={() => setDescriptionHtml("")}
                        >
                            <X size={18} />
                        </button> */}

                        <div className="ppm-description-header">

                            <div>

                                <h2>AI Scope of Work</h2>

                                <span>
                                    Automatically generated proposal details
                                </span>

                            </div>

                            <button
                                className="ppm-close"
                                onClick={() => setDescriptionHtml("")}
                            >
                                <X size={18} />
                            </button>

                        </div>

                        <div className="ppm-description-paper">

                            <div
                                className="ppm-description-content"
                                dangerouslySetInnerHTML={{
                                    __html: descriptionHtml
                                }}
                            />

                        </div>

                    </div>

                </div>

            )}
        </div>

    );

}